import { getSubscriptionActive } from "@service/subscription";
import { SubscriptionActive } from "@type/subscription";
import { isTrialGraceExpired } from "@constant/subscription";
import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import { useUserProfileContext } from "@context/user_profile";
import { resolveBillingPlatform } from "@util/billing-platform";
import { maxTrialUsagePercent } from "@util/subscription-usage";
import { SnackbarProvider } from "@hook/use-snackbar";
import TrialWarningDialog from "@container/dialog/dialog-trial-warning";
import DialogViewPlan from "@container/dialog/dialog-view-plan";

type TrialWarningType = "ending_soon" | "usage_limit" | "usage_limit_ending_soon";

interface SubscriptionContextType {
    subscriptionActive: SubscriptionActive | null;
    isLoadingSubscriptionActive: boolean;
    reminderExpiredDay: number;
    /**
     * True when the trial has expired AND the 7-day grace window after
     * `ended_at` has fully passed. Use this to gate destructive/mutation
     * actions (status changes, edits, deletes, etc.) — the workspace is
     * effectively read-only at this point.
     *
     * The Create/Edit Job, Bulk CV upload and Create/Edit Assessment buttons
     * are gated on the derived `isJobActionBlocked` / `isAssessmentActionBlocked`
     * flags below, which build on this (past-grace restriction) AND the trial
     * usage caps.
     */
    isWorkspaceRestricted: boolean;
    /**
     * True when Job create/edit and bulk CV upload should be disabled: the
     * workspace is restricted (trial expired + past grace), the trial
     * CV-screening cap is hit, the trial AI-assessment cap is hit, OR a paid
     * plan (is_trial=false) has lapsed (is_expired).
     */
    isJobActionBlocked: boolean;
    /**
     * True when Assessment create/edit AND sending assessment invitations /
     * reminders should be disabled: the workspace is restricted (trial expired
     * + past grace), the trial AI-assessment cap is hit, OR a paid plan
     * (is_trial=false) has lapsed (is_expired). (CV cap alone does not block
     * these.)
     */
    isAssessmentActionBlocked: boolean;
    /**
     * Training-platform action gates (training-agent workspaces). Each is true
     * when the workspace is restricted (trial expired + past grace), a paid
     * plan has lapsed, OR that action's own Training usage cap is reached
     * (BE #3215). Mirror of the recruiter `isJobActionBlocked` shape.
     *   - Campaign: create / edit a training campaign
     *   - Roleplay: start / trainer-test a roleplay (practice) session
     *   - Aiqm:     upload AIQM call audio for scoring
     */
    isTrainingCampaignBlocked: boolean;
    isTrainingRoleplayBlocked: boolean;
    isTrainingAiqmBlocked: boolean;
    /** Upload a Knowledge Base document — blocked on restriction / lapsed plan /
     *  the Training document-upload cap (BE #3253). */
    isTrainingDocumentUploadBlocked: boolean;
    refetchSubscription: () => void;
    showTrialUsageLimitDialog: (isEndingSoon?: boolean) => void;
    openViewPlanDialog: () => void;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

const TRIAL_ENDING_DISMISSED_KEY = "trial_ending_soon_dismissed";
const TRIAL_ENDING_EXPIRY_MS = 2 * 60 * 60 * 1000; // 2 hours

const isTrialEndingDismissed = (): boolean => {
    const dismissedAt = sessionStorage.getItem(TRIAL_ENDING_DISMISSED_KEY);
    if (!dismissedAt) return false;
    return Date.now() - Number(dismissedAt) < TRIAL_ENDING_EXPIRY_MS;
};

const setTrialEndingDismissed = () => {
    sessionStorage.setItem(TRIAL_ENDING_DISMISSED_KEY, String(Date.now()));
};

type Currency = "SGD" | "MYR" | "IDR" | "USD";

const currencyPricing: Record<Currency, {
    symbol: string;
    plans: { "12month": number; "12monthTotal": number; "6month": number; "6monthTotal": number };
    payAsYouGo: { cvScreening: number; aiAssessment: number };
}> = {
    SGD: { symbol: "SGD", plans: { "12month": 269, "12monthTotal": 3229, "6month": 299, "6monthTotal": 899 }, payAsYouGo: { cvScreening: 1, aiAssessment: 5 } },
    MYR: { symbol: "RM", plans: { "12month": 899, "12monthTotal": 10789, "6month": 999, "6monthTotal": 2997 }, payAsYouGo: { cvScreening: 3, aiAssessment: 15 } },
    IDR: { symbol: "IDR", plans: { "12month": 3150000, "12monthTotal": 37800000, "6month": 3500000, "6monthTotal": 10500000 }, payAsYouGo: { cvScreening: 10000, aiAssessment: 50000 } },
    USD: { symbol: "USD", plans: { "12month": 199, "12monthTotal": 2389, "6month": 219, "6monthTotal": 657 }, payAsYouGo: { cvScreening: 1, aiAssessment: 5 } },
};

export const SubscriptionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [, userInfo] = useUserProfileContext();
    const [subscriptionActive, setSubscriptionActive] = useState<SubscriptionActive | null>(null);
    const [isLoadingSubscriptionActive, setIsLoadingSubscriptionActive] = useState(true);
    const [reminderExpiredDay, setReminderExpiredDay] = useState(0);
    const [showTrialWarningDialog, setShowTrialWarningDialog] = useState(false);
    const [trialWarningType, setTrialWarningType] = useState<TrialWarningType>("ending_soon");
    const [showViewPlanDialog, setShowViewPlanDialog] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState<"12month" | "6month" | null>("12month");
    const [requestInfoNote, setRequestInfoNote] = useState("");

    const fetchSubscription = () => {
        setIsLoadingSubscriptionActive(true);
        getSubscriptionActive()
            .then((resp) => {
                if (resp.status === 200) {
                    const data = resp.data.data;
                    setSubscriptionActive(data);

                    // Compute days remaining locally — same approach the
                    // TrialUsageBanner uses, so the dialog and the banner
                    // share one source of truth for "ending soon".
                    let daysRemaining = 0;
                    if (data.ended_at) {
                        const endedAt = new Date(data.ended_at);
                        const today = new Date();
                        const end = new Date(endedAt.getFullYear(), endedAt.getMonth(), endedAt.getDate());
                        const current = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                        const diffTime = end.getTime() - current.getTime();
                        const diffDays = Math.ceil(diffTime / (1000 * 3600 * 24));
                        daysRemaining = Math.max(0, diffDays);
                        setReminderExpiredDay(daysRemaining);
                    }

                    // Aligned with TrialUsageBanner:
                    //   - usageHigh: max(cv, ai) usage percentage ≥ 80
                    //   - daysLow:  finite days remaining ≤ 3
                    const USAGE_THRESHOLD = 80;
                    const DAYS_THRESHOLD = 3;
                    // Worst cap utilisation across whichever caps this
                    // platform exposes (recruiter CV/AI, or Training
                    // session/campaign/AIQM) — shared with TrialUsageBanner.
                    const usagePct = Math.round(maxTrialUsagePercent(data));
                    const hasMeaningfulRemainingDays = Boolean(data.ended_at);
                    const usageHigh = usagePct >= USAGE_THRESHOLD;
                    const daysLow =
                        hasMeaningfulRemainingDays && daysRemaining <= DAYS_THRESHOLD;

                    // Trial-only AND not yet expired: paid plans never see this
                    // copy; expired trials are handled by TrialUsageBanner with
                    // the "trial has expired" wording, so the dialog should stay
                    // out of their way.
                    if (data.is_trial && !data.is_expired) {
                        // Mutually-exclusive variants — only one fires:
                        //   1. ending_soon              — daysLow only
                        //   2. usage_limit              — usageHigh only
                        //   3. usage_limit_ending_soon  — usageHigh AND daysLow
                        if (usageHigh && daysLow) {
                            setTrialWarningType("usage_limit_ending_soon");
                            setShowTrialWarningDialog(true);
                        } else if (usageHigh) {
                            setTrialWarningType("usage_limit");
                            setShowTrialWarningDialog(true);
                        } else if (daysLow && !isTrialEndingDismissed()) {
                            setTrialWarningType("ending_soon");
                            setShowTrialWarningDialog(true);
                        }
                    }
                }
            })
            .finally(() => {
                setIsLoadingSubscriptionActive(false);
            });
    };

    useEffect(() => {
        // Recruiter (HR) behaviour is unchanged; additionally fetch for a
        // Training (`training-agent`) workspace so its trial banner + warning
        // dialog light up. `resolveBillingPlatform` requires both role AND
        // company_type === "training-agent" (mirrors the Training nav gate),
        // so other recruiter roles (admin/super_admin) and agents are
        // deliberately left as-is (#2686).
        const isTraining = resolveBillingPlatform(userInfo) === "training";
        if (userInfo?.role === "hr" || isTraining) {
            fetchSubscription();
        } else {
            setIsLoadingSubscriptionActive(false);
        }
    }, [userInfo?.role, userInfo?.company_type]);

    const formatPrice = useCallback((price: number): string => {
        const currency = (subscriptionActive?.company_currency as Currency) || "MYR";
        const { symbol } = currencyPricing[currency];
        if (currency === "IDR") {
            return `${symbol} ${Math.round(price).toLocaleString()}`;
        }
        return `${symbol} ${price.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
    }, [subscriptionActive?.company_currency]);

    const getPlanMonthlyPrice = useCallback((plan: "12month" | "6month"): string => {
        const currency = (subscriptionActive?.company_currency as Currency) || "MYR";
        return formatPrice(currencyPricing[currency].plans[plan]);
    }, [subscriptionActive?.company_currency, formatPrice]);

    const getPlanTotalPrice = useCallback((plan: "12month" | "6month"): string => {
        const currency = (subscriptionActive?.company_currency as Currency) || "MYR";
        const priceKey = plan === "12month" ? "12monthTotal" : "6monthTotal";
        return formatPrice(currencyPricing[currency].plans[priceKey]);
    }, [subscriptionActive?.company_currency, formatPrice]);

    const getPayAsYouGoPrice = useCallback((type: "cvScreening" | "aiAssessment"): string => {
        const currency = (subscriptionActive?.company_currency as Currency) || "MYR";
        return formatPrice(currencyPricing[currency].payAsYouGo[type]);
    }, [subscriptionActive?.company_currency, formatPrice]);

    // True when the *trial* expired AND the grace window after `ended_at` has
    // fully passed. Paid plans (is_trial=false) never enter this restricted
    // state (they're gated by `paidExpired` below instead).
    let isWorkspaceRestricted = false;
    if (
        subscriptionActive?.is_trial
        && subscriptionActive?.is_expired
        && subscriptionActive.ended_at
    ) {
        const endedAt = new Date(subscriptionActive.ended_at);
        const today = new Date();
        const end = new Date(endedAt.getFullYear(), endedAt.getMonth(), endedAt.getDate());
        const current = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const daysSinceEnd = Math.floor((current.getTime() - end.getTime()) / (1000 * 3600 * 24));
        isWorkspaceRestricted = isTrialGraceExpired(daysSinceEnd);
    }

    // Trial usage caps (only meaningful on trial plans — paid plans bypass).
    // CV caps block Job create/edit; AI caps block Assessment create/edit.
    const isTrial = !!subscriptionActive?.is_trial;
    const cvLimitReached =
        isTrial && (
            !!subscriptionActive?.is_cv_screening_limit_reached
            || !!subscriptionActive?.is_cv_limit_reached
        );
    const aiLimitReached =
        isTrial && (
            !!subscriptionActive?.is_ai_usage_limit_reached
            || !!subscriptionActive?.is_ai_limit_reached
        );
    // Paid plan (is_trial=false) whose billing period has lapsed (is_expired) —
    // its create/edit entry points are blocked until the subscription is
    // renewed. Paid plans have no grace window (unlike trials), so this fires
    // the moment `is_expired` is true.
    const paidExpired = !isTrial && !!subscriptionActive?.is_expired;

    // Job create/edit + bulk CV upload: blocked by the past-grace restriction,
    // the CV-screening cap, the AI-assessment cap, OR a lapsed paid plan. (CV
    // parsing limit == AI-assessment completed limit, so once the AI cap is hit
    // the CV-side entry points should already be off too.)
    const isJobActionBlocked =
        isWorkspaceRestricted || cvLimitReached || aiLimitReached || paidExpired;
    // Assessment create/edit AND sending assessment invitations/reminders:
    // blocked by the past-grace restriction, the AI-assessment cap, OR a lapsed
    // paid plan. (CV cap alone does NOT block these — invitations only consume
    // AI quota.)
    const isAssessmentActionBlocked =
        isWorkspaceRestricted || aiLimitReached || paidExpired;

    // Training plan caps apply to BOTH trial and PAID training plans (#2725): the
    // BE `is_training_*_limit_reached` flag only trips when there is a real cap and
    // it's been hit, so we trust it directly rather than gating on `isTrial`. (#3215
    // was trial-only; #3253 extends caps to paid training plans like "3 Months".)
    // Each gate also fires on the past-grace restriction or a lapsed paid plan.
    const trainingCampaignLimitReached =
        !!subscriptionActive?.is_training_campaign_limit_reached;
    const trainingSessionLimitReached =
        !!subscriptionActive?.is_training_session_limit_reached;
    const trainingAiqmLimitReached =
        !!subscriptionActive?.is_training_audio_upload_limit_reached
        || !!subscriptionActive?.is_aiqm_upload_limit_reached;
    const trainingDocumentUploadLimitReached =
        !!subscriptionActive?.is_training_document_upload_limit_reached
        || !!subscriptionActive?.is_document_upload_limit_reached;
    // Create/edit a training campaign.
    const isTrainingCampaignBlocked =
        isWorkspaceRestricted || paidExpired || trainingCampaignLimitReached;
    // Start / trainer-test a roleplay (practice) session.
    const isTrainingRoleplayBlocked =
        isWorkspaceRestricted || paidExpired || trainingSessionLimitReached;
    // Upload AIQM call audio for scoring.
    const isTrainingAiqmBlocked =
        isWorkspaceRestricted || paidExpired || trainingAiqmLimitReached;
    // Upload a Knowledge Base document (consumer page lands on staging — exposed
    // here so it's ready when the KB page reaches this branch).
    const isTrainingDocumentUploadBlocked =
        isWorkspaceRestricted || paidExpired || trainingDocumentUploadLimitReached;

    return (
        <SubscriptionContext.Provider
            value={{
                subscriptionActive,
                isLoadingSubscriptionActive,
                reminderExpiredDay,
                isWorkspaceRestricted,
                isJobActionBlocked,
                isAssessmentActionBlocked,
                isTrainingCampaignBlocked,
                isTrainingRoleplayBlocked,
                isTrainingAiqmBlocked,
                isTrainingDocumentUploadBlocked,
                refetchSubscription: fetchSubscription,
                showTrialUsageLimitDialog: (isEndingSoon?: boolean) => {
                    setTrialWarningType(isEndingSoon ? "ending_soon" : "usage_limit");
                    setShowTrialWarningDialog(true);
                },
                openViewPlanDialog: () => {
                    setShowViewPlanDialog(true);
                },
            }}
        >
            {children}
            <TrialWarningDialog
                isOpen={showTrialWarningDialog && !!subscriptionActive?.is_trial && !subscriptionActive?.is_expired}
                onClose={() => {
                    setShowTrialWarningDialog(false);
                    setTrialEndingDismissed();
                }}
                onViewPricingPlans={() => {
                    setShowTrialWarningDialog(false);
                    setTrialEndingDismissed();
                    setShowViewPlanDialog(true);
                }}
                type={trialWarningType}
                daysRemaining={reminderExpiredDay}
                isTraining={resolveBillingPlatform(userInfo) === "training"}
            />
            <SnackbarProvider>
                <DialogViewPlan
                    open={showViewPlanDialog}
                    onOpenChange={setShowViewPlanDialog}
                    selectedPlan={selectedPlan}
                    setSelectedPlan={setSelectedPlan}
                    requestInfoNote={requestInfoNote}
                    setRequestInfoNote={setRequestInfoNote}
                    getPlanMonthlyPrice={getPlanMonthlyPrice}
                    getPlanTotalPrice={getPlanTotalPrice}
                    getPayAsYouGoPrice={getPayAsYouGoPrice}
                />
            </SnackbarProvider>
        </SubscriptionContext.Provider>
    );
};

export const useSubscription = () => {
    const context = useContext(SubscriptionContext);
    if (context === undefined) {
        throw new Error("useSubscription must be used within a SubscriptionProvider");
    }
    return context;
};
